import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import {
  buildConditionalAccessBaselineRows,
  buildConditionalAccessPolicyStats,
  getPolicyName,
  isEnforcedPolicy,
  isReportOnlyPolicy,
} from '../../data/conditionalAccessBaseline'
import { TONE } from '../../data/constants'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'
import styles from './IdentityAccessSection.module.scss'

const EMPTY_CELL = '-'

const getEnabledPolicyStatsTone = ({ enabled, total }) => {
  if (total === 0) return TONE.NEUTRAL
  if (enabled === total) return TONE.OK
  return TONE.WARN
}

export default function ConditionalAccess({ score, summary, policies = [], isLoading = false, isError = false }) {
  const rows = buildConditionalAccessBaselineRows(policies)
  const policyStats = buildConditionalAccessPolicyStats({ policies, summary: summary.conditionalAccess })

  const stats = [
    {
      key: 'enabled',
      value: policyStats.enabled,
      denom: policyStats.total,
      tone: getEnabledPolicyStatsTone(policyStats),
      label: (
        <FormattedMessage
          id="AI_RISK_REPORT.CONDITIONAL_ACCESS.ENABLED_NOTE"
          values={{ pct: policyStats.enabledPct }}
        />
      ),
    },
    {
      key: 'reportOnly',
      value: policyStats.reportOnly,
      tone: policyStats.reportOnly > 0 ? TONE.WARN : TONE.OK,
      label: <FormattedMessage id="AI_RISK_REPORT.CONDITIONAL_ACCESS.REPORT_ONLY_NOTE" />,
    },
    {
      key: 'total',
      value: policyStats.total,
      label: <FormattedMessage id="AI_RISK_REPORT.CONDITIONAL_ACCESS.TOTAL_POLICIES" />,
    },
  ]

  const columns = [
    {
      key: 'pattern',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_PATTERN" />,
      render: (row) => (
        <span className="text-body fw-bold">
          <FormattedMessage id={row.pattern.labelId} />
        </span>
      ),
      sortable: true,
      sortValue: (row) => row.pattern.labelId,
    },
    {
      key: 'policy',
      header: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.BASELINE.COL_MATCHED_POLICY" />,
      render: (row) =>
        row.bestPolicy ? (
          <span>{getPolicyName(row.bestPolicy)}</span>
        ) : (
          <span className="text-muted">
            <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.BASELINE.NO_MATCH" />
          </span>
        ),
      sortable: true,
      sortValue: (row) => getPolicyName(row.bestPolicy) || '',
    },
    {
      key: 'coverage',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_COVERAGE" />,
      render: (row) => {
        if (!row.bestPolicy) return EMPTY_CELL

        return (
          <div className="d-grid gap-2">
            <div className={styles.coverageRequirementsCount}>
              <FormattedMessage
                id="AI_RISK_REPORT.IDENTITY.CA.REQ.PASS_COUNT"
                values={{ passed: row.passed, total: row.total }}
              />
            </div>
            <div className="d-flex flex-wrap gap-2">
              {row.requirements.map((requirement) => (
                <Chip
                  key={requirement.key}
                  tone={requirement.met ? TONE.OK : TONE.DANGER}
                  className="d-inline-flex align-items-center gap-1"
                >
                  <i
                    className={`fas fa-${requirement.met ? 'check' : 'xmark'} ${styles.requirementIcon}`}
                    aria-hidden="true"
                  />
                  <FormattedMessage id={requirement.labelId} />
                </Chip>
              ))}
            </div>
          </div>
        )
      },
    },
    {
      key: 'status',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_STATUS" />,
      className: styles.statusColumn,
      render: (row) => {
        if (!row.bestPolicy) return EMPTY_CELL
        if (isEnforcedPolicy(row.bestPolicy)) return <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.STATE_ENFORCED" />
        if (isReportOnlyPolicy(row.bestPolicy))
          return <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.STATE_REPORT_ONLY" />
        return <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.STATE_DISABLED" />
      },
      sortable: true,
      sortValue: (row) => {
        if (!row.bestPolicy) return 'zzz'
        if (isEnforcedPolicy(row.bestPolicy)) return 'enforced'
        if (isReportOnlyPolicy(row.bestPolicy)) return 'reportOnly'
        return 'disabled'
      },
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.IDENTITY.CA.TITLE"
      subtitleId="AI_RISK_REPORT.IDENTITY.CA.SUBTITLE"
      score={score.value}
      tone={score.tone}
      stats={stats}
    >
      {isLoading && <EmptyState messageId="AI_RISK_REPORT.LOADING" />}
      {!isLoading && isError && <EmptyState messageId="AI_RISK_REPORT.IDENTITY.CA.ERROR" />}
      {!isLoading && !isError && <ReportTable columns={columns} rows={rows} />}
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  conditionalAccess: PropTypes.shape({
    total: PropTypes.number,
    enabled: PropTypes.number,
    reportOnly: PropTypes.number,
    enabledPct: PropTypes.number,
  }),
})

ConditionalAccess.propTypes = {
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  summary: summaryPropType.isRequired,
  policies: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
}
